import { QueryV1, BoolQueryV1, TermsQueryV1, RangeQueryV1 } from '@ttab/elephant-api/index'
import type { WireFilter } from '../hooks/useWireViewState'
import { buildAdvancedQuery } from '@/components/AdvancedSearch/lib/buildQuery'
import { parseAdvancedSearchJson } from '@/components/AdvancedSearch/lib/defaultState'
import { buildFreeTextQuery } from '@/components/AdvancedSearch/lib/freeTextQuery'
import { wiresFields, dateFields } from '@/components/AdvancedSearch/configs'

/**
 * Constructs a query object based on the provided filter parameters.
 *
 * @param {WireFilter[]} filters - The filter parameters to construct the query.
 * @param {string} timeZone - Timezone the date range's day boundaries are read in.
 * @returns {QueryV1 | undefined} - The constructed query object or undefined if no filter is provided.
 */
export function constructQuery(filters: WireFilter[], timeZone: string): QueryV1 | undefined {
  if (!filters.length) {
    return
  }

  const query = QueryV1.create({
    conditions: {
      oneofKind: 'bool',
      bool: BoolQueryV1.create({
        must: []
      })
    }
  })

  if (query.conditions.oneofKind !== 'bool') {
    return
  }

  const boolConditions = query.conditions.bool

  const addCondition = (field: string, values: string | string[]) => {
    boolConditions.must.push({
      conditions: {
        oneofKind: 'terms',
        terms: TermsQueryV1.create({
          field,
          values: typeof values === 'string' ? [values] : values
        })
      }
    })
  }

  filters.forEach((filter) => {
    if (filter.type === 'core/source') {
      addCondition('document.rel.source.uri', filter.values)
    } else if (filter.type === 'core/section') {
      addCondition('document.rel.section.uuid', filter.values)
    } else if (filter.type === 'core/newsvalue') {
      addCondition('document.meta.core_newsvalue.value', filter.values)
    } else if (filter.type === 'wireStatus') {
      const statusConditions = filter.values.map((status) =>
        QueryV1.create({

          conditions: {
            oneofKind: 'range',
            range: RangeQueryV1.create({ field: `heads.${status}.version`, gte: '1' })
          }
        }))

      boolConditions.must.push(QueryV1.create({
        conditions: {
          oneofKind: 'bool',
          bool: BoolQueryV1.create({
            should: statusConditions,
            minimumShouldMatch: BigInt(1)
          })
        }
      }))
    } else if (filter.type === 'advancedSearch') {
      if (!filter.values[0]) return
      const advState = parseAdvancedSearchJson(filter.values[0], 'constructQuery', wiresFields)
      if (advState) {
        const advQuery = buildAdvancedQuery(advState, wiresFields, { field: dateFields.wires, timeZone })
        if (advQuery) {
          boolConditions.must.push(advQuery)
        }
      }
    } else if (filter.type === 'query') {
      filter.values.forEach((value) => {
        const freeText = buildFreeTextQuery(value, wiresFields, [
          'document.title',
          'document.content.core_text.data.text',
          'document.content.core_table.data.tbody',
          'document.content.core_table.data.thead',
          'document.content.core_table.data.tfoot',
          'document.content.core_table.data.caption',
          'document.content.core_ordered_list.content.core_text.data.text',
          'document.content.core_unordered_list.content.core_text.data.text'
        ])
        if (freeText) {
          boolConditions.must.push(freeText)
        }
      })
    }
  })

  return query
}
